import { getNamespacedResource, isModuleRegistered } from "SharedStores/util";
import { PROGRAM_NAMESPACE, PROGRAM_TYPES } from "SharedStores/Program/programStore";
import { DATA_FIELD_GROUP_TYPE } from "Types/dataFieldTypes";
import { SIGNATORY_CHOICE_CONSTANTS } from "ESignatures/eSignatureConstants";

const ESIGNATURE_NAMESPACE = "eSignature";

const GET_RECIPIENT_ADJACENT_CONTACT_FIELD_SUGGESTIONS = "getRecipientAdjacentContactFieldSuggestions";
const ESIGNATURE_TYPES = {
    getters: {
        [GET_RECIPIENT_ADJACENT_CONTACT_FIELD_SUGGESTIONS]: getNamespacedResource(
            ESIGNATURE_NAMESPACE,
            GET_RECIPIENT_ADJACENT_CONTACT_FIELD_SUGGESTIONS
        )
    }
};

export { ESIGNATURE_NAMESPACE, ESIGNATURE_TYPES };

export function createESignatureModule() {
    return {
        namespaced: true,
        state: {}, // Currently no state
        getters: {
            /**
             * When a single recipient contact info field changes, we may want to default
             * other fields to the corresponding ones in the same group. For example, if a user
             * selects a new First Name for a recipient and there's no last name, we want to
             * default to show the corresponding Last Name from the same Name or Contact group.
             * This method will output suggestions for what any other fields should be changed to;
             * it is up to the caller to commit those suggestions in the appropriate way for the
             * context
             */
            [GET_RECIPIENT_ADJACENT_CONTACT_FIELD_SUGGESTIONS](state, getters, rootState, rootGetters) {
                return function ({ updatedFieldType, updatedFieldId }) {
                    if (!isModuleRegistered(PROGRAM_NAMESPACE, rootState)) {
                        throw "eSignatureStore: Expected program store to be registered";
                    }

                    // No defaults to recommend if changing a "User" field, since all contact info
                    // encapsulated by that one field
                    if (updatedFieldType === SIGNATORY_CHOICE_CONSTANTS.UserDataFields) {
                        return [];
                    }

                    // If deselecting a field, don't recommend any updates to other fields
                    if (!updatedFieldId) {
                        return [];
                    }

                    function getFieldById(fieldId) {
                        return rootGetters[PROGRAM_TYPES.getters.dataFieldDefinitionFromId](fieldId);
                    }
                    function getGroupById(groupId) {
                        return rootGetters[PROGRAM_TYPES.getters.dataFieldGroupDefinitionFromId](groupId);
                    }

                    const suggestedFields = [];
                    function tryAddFieldSuggestion(type, fieldId) {
                        if (!fieldId) {
                            return;
                        }
                        suggestedFields.push({
                            type,
                            value: fieldId
                        });
                    }

                    const recipientFieldOptions = rootGetters[PROGRAM_TYPES.getters.getSignatoryContactFieldsByType];
                    const firstNameFieldOptions = recipientFieldOptions[SIGNATORY_CHOICE_CONSTANTS.FirstNameDataFields];
                    const lastNameFieldOptions = recipientFieldOptions[SIGNATORY_CHOICE_CONSTANTS.LastNameDataFields];
                    const emailFieldOptions = recipientFieldOptions[SIGNATORY_CHOICE_CONSTANTS.EmailDataFields];

                    let group = null;

                    // For first or last name, find the group it belongs to and add a suggestion
                    // for the opposite piece of the name for the corresponding field in that group
                    if (updatedFieldType === SIGNATORY_CHOICE_CONSTANTS.FirstNameDataFields) {
                        const firstField = getFieldById(updatedFieldId);
                        group = getGroupById(firstField.DataFieldGroupId);
                        const lastFieldId = findGroupedFieldId(group, lastNameFieldOptions);

                        tryAddFieldSuggestion(SIGNATORY_CHOICE_CONSTANTS.LastNameDataFields, lastFieldId);
                    } else if (updatedFieldType === SIGNATORY_CHOICE_CONSTANTS.LastNameDataFields) {
                        const lastField = getFieldById(updatedFieldId);
                        group = getGroupById(lastField.DataFieldGroupId);
                        const firstFieldId = findGroupedFieldId(group, firstNameFieldOptions);

                        tryAddFieldSuggestion(SIGNATORY_CHOICE_CONSTANTS.FirstNameDataFields, firstFieldId);
                    }
                    // For an email field, if it's part of a contact group (as opposed to standalone),
                    // add suggestions for the corresponding first and last name fields
                    else if (updatedFieldType === SIGNATORY_CHOICE_CONSTANTS.EmailDataFields) {
                        const emailField = getFieldById(updatedFieldId);
                        group = getGroupById(emailField.DataFieldGroupId);
                        if (group && group.TypeId === DATA_FIELD_GROUP_TYPE.Contact) {
                            const firstFieldId = findGroupedFieldId(group, firstNameFieldOptions);
                            const lastFieldId = findGroupedFieldId(group, lastNameFieldOptions);

                            tryAddFieldSuggestion(SIGNATORY_CHOICE_CONSTANTS.FirstNameDataFields, firstFieldId);
                            tryAddFieldSuggestion(SIGNATORY_CHOICE_CONSTANTS.LastNameDataFields, lastFieldId);
                        }
                    } else {
                        throw `Unexpected updated field type ${updatedFieldType}`;
                    }

                    // Suggest matching email if names are from a contact group
                    if (
                        (updatedFieldType === SIGNATORY_CHOICE_CONSTANTS.FirstNameDataFields ||
                            updatedFieldType === SIGNATORY_CHOICE_CONSTANTS.LastNameDataFields) &&
                        group.TypeId === DATA_FIELD_GROUP_TYPE.Contact
                    ) {
                        const emailFieldId = findGroupedFieldId(group, emailFieldOptions);
                        tryAddFieldSuggestion(SIGNATORY_CHOICE_CONSTANTS.EmailDataFields, emailFieldId);
                    }

                    return suggestedFields;
                };
            }
        }
    };
}

function findGroupedFieldId(dataFieldGroupDefinition, options) {
    const matchingField = options.find((option) => {
        return (
            option.text.includes(dataFieldGroupDefinition.Name) &&
            dataFieldGroupDefinition.DataFieldIds.includes(option.value)
        );
    });
    return matchingField?.value ?? null;
}
